import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const pendingForms = new WeakSet();

const selectedAddons = (form) => [...form.querySelectorAll('[data-varevo-addon]:checked')].filter((input) => !input.disabled && input.value);

function updateAddonControls(form) {
  if (!form) return;
  const none = form.querySelector('[data-varevo-bags-none]');
  if (none) {
    const bags = [...form.querySelectorAll('[data-varevo-addon-group="bags"]')];
    none.setAttribute('aria-checked', String(!bags.some(input => input.checked)));
    none.disabled = bags.length > 0 && bags.every(input => input.disabled);
  }
  form.querySelectorAll('.vp-box-card').forEach((card) => {
    const input = card.querySelector('[data-varevo-addon]');
    const add = card.querySelector('[data-varevo-addon-add]');
    const remove = card.querySelector('[data-varevo-addon-remove]');
    if (!input) return;
    if (add) {
      add.disabled = input.disabled || input.checked;
      add.setAttribute('aria-pressed', String(input.checked));
      const label = input.dataset.varevoAddonLabel || 'bokssett';
      add.setAttribute('aria-label', input.checked ? label + ' valgt' : 'Legg til ' + label);
    }
    if (remove) remove.disabled = input.disabled;
  });
}

window.addEventListener('click', (event) => {
  const selection = event.target.closest?.('[data-varevo-bags-none], [data-varevo-addon-add], [data-varevo-addon-remove]');
  if (!selection || !selection.closest('form[data-varevo-cart="true"]')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const none = selection.matches('[data-varevo-bags-none]') ? selection : null;
  if (none) {
    if (none.disabled) return;
    const form = none.closest('form');
    const inputs = [...form.querySelectorAll('[data-varevo-addon-group="bags"]')];
    if (inputs.some(input => input.checked)) {
      inputs.forEach(input => { input.checked = false; });
    } else {
      const first = inputs.find(input => !input.disabled);
      if (first) first.checked = true;
    }
    updateAddonTotal(form);
    return;
  }
  const control = event.target.closest?.('[data-varevo-addon-add], [data-varevo-addon-remove]');
  if (!control || control.disabled) return;
  const input = control.closest('.vp-box-card')?.querySelector('[data-varevo-addon]');
  if (!input || input.disabled) return;
  const adding = control.hasAttribute('data-varevo-addon-add');
  if (adding && input.dataset.varevoAddonGroup) {
    input.form.querySelectorAll('[data-varevo-addon]').forEach((other) => {
      if (other !== input && other.dataset.varevoAddonGroup === input.dataset.varevoAddonGroup) other.checked = false;
    });
  }
  input.checked = adding;
  input.dispatchEvent(new Event('change', { bubbles: true }));
}, { capture: true });

function updateAddonTotal(form) {
  updateAddonControls(form);
  const total = form.querySelector('[data-varevo-order-total]');
  if (!total) return;
  const base = Number(form.dataset.varevoBasePrice) || 0;
  let amount = base;
  let before = Math.max(base, Number(form.dataset.varevoBaseCompare) || 0);
  for (const input of selectedAddons(form)) {
    const price = Number(input.dataset.varevoAddonPrice) || 0;
    amount += price;
    before += Math.max(price, Number(input.dataset.varevoAddonCompare) || 0);
  }
  const money = (value) => new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value / 100);
  total.textContent = money(amount);
  const comparison = form.querySelector('[data-varevo-order-compare]');
  const saving = form.querySelector('[data-varevo-order-saving]');
  if (comparison) comparison.textContent = before > amount ? money(before) : '';
  if (saving) saving.textContent = before > amount ? 'Du sparer ' + money(before - amount) : '';
}

function initializeAddons() {
  document.querySelectorAll('form[data-varevo-cart="true"]').forEach((form) => {
    form.querySelectorAll('[data-varevo-addon]').forEach((input) => {
      input.disabled = input.dataset.varevoAddonAvailable !== 'true';
    });
    if (!form.querySelector('[data-varevo-bags-none]')) {
      const bags = [...form.querySelectorAll('[data-varevo-addon-group="bags"]')];
      if (!bags.some(input => input.checked && !input.disabled)) {
        bags.forEach(input => { input.checked = false; });
        const first = bags.find(input => !input.disabled);
        if (first) first.checked = true;
      }
    }
    updateAddonTotal(form);
  });
}

document.addEventListener('change', (event) => {
  if (event.target.matches?.('[data-varevo-addon]')) updateAddonTotal(event.target.form);
});
document.addEventListener('shopify:section:load', initializeAddons);
window.addEventListener('pageshow', initializeAddons);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAddons, { once: true });
else initializeAddons();


async function submitVarevoCart(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.dataset.varevoCart !== 'true') return;
  const drawer = document.getElementById('cart-drawer');
  const addons = selectedAddons(form);
  if (!drawer && addons.length === 0) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (pendingForms.has(form)) return;

  const data = new FormData(form);
  const variantId = data.get('id');
  if (!variantId) return;
  const quantity = Number(data.get('quantity')) || 1;
  const cartLines = [{ merchandiseId: String(variantId), quantity }, ...addons.map((input) => ({ merchandiseId: String(input.value), quantity: 1 }))];
  const bagAddon = addons.find(input => input.dataset.varevoAddonGroup === 'bags');
  if (bagAddon) {
    const bundleId = window.crypto.randomUUID();
    const bagLine = cartLines.find(line => line.merchandiseId === String(bagAddon.value));
    const title = 'MatSpar+ ' + (bagAddon.dataset.varevoAddonLabel || '+ poser');
    const properties = { _varevo_bundle: bundleId, _varevo_bundle_title: title };
    cartLines[0].properties = properties;
    if (bagLine) {
      bagLine.quantity = quantity;
      bagLine.properties = properties;
    }
  }
  const addedCount = cartLines.reduce((sum, line) => sum + line.quantity, 0);
  const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId))].slice(0, 5);
  if (sectionIds.length) data.set('sections', sectionIds.join(','));
  data.set('sections_url', window.location.pathname + (window.varevoPreviewThemeId ? '?preview_theme_id=' + encodeURIComponent(window.varevoPreviewThemeId) : ''));

  let status = form.querySelector('[data-varevo-cart-status]');
  if (!status) {
    status = document.createElement('p');
    status.dataset.varevoCartStatus = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.style.cssText = 'font-size:14px;line-height:1.5;margin:12px 0;color:#203b32';
    form.append(status);
  }
  status.textContent = '';
  const buttons = [...form.querySelectorAll('button[type="submit"], [data-varevo-buy]')];
  const initialDisabled = buttons.map((button) => button.disabled);
  const addonInputs = [...form.querySelectorAll('[data-varevo-addon]')];
  const initialAddonDisabled = addonInputs.map((input) => input.disabled);
  pendingForms.add(form);
  addonInputs.forEach((input) => { input.disabled = true; });
  updateAddonControls(form);
  buttons.forEach((button) => { button.disabled = true; });
  form.setAttribute('aria-busy', 'true');

  const deferred = CartLinesUpdateEvent.createPromise();
  deferred.promise.catch(() => {});
  form.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: cartLines,
    promise: deferred.promise,
  }));

  let productAdded = false;
  try {
    const addUrl = new URL(form.action, window.location.href);
    addUrl.pathname = addUrl.pathname.replace(/\/add(?:\.js)?$/, '/add.js');
    const request = addons.length ? {
      method: 'POST',
      body: JSON.stringify({
        items: cartLines.map((line) => ({ id: Number(line.merchandiseId), quantity: line.quantity, ...(line.properties ? { properties: line.properties } : {}) })),
        sections: sectionIds.join(','),
        sections_url: window.location.pathname + (window.varevoPreviewThemeId ? '?preview_theme_id=' + encodeURIComponent(window.varevoPreviewThemeId) : ''),
      }),
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    } : { method: 'POST', body: data, credentials: 'same-origin', headers: { Accept: 'application/json' } };
    const response = await fetch(addUrl.toString(), request);
    const added = await response.json();
    if (!response.ok || added.status) {
      throw new Error(typeof added.description === 'string' ? added.description : 'Produktet kunne ikke legges i handlekurven. Prøv igjen.');
    }
    productAdded = true;
    const cartUrl = new URL(addUrl);
    cartUrl.pathname = cartUrl.pathname.replace(/\/add\.js$/, '.js');
    if (!drawer) {
      deferred.resolve({ detail: { sections: added.sections, itemCount: addedCount, source: 'varevo-product-form', didError: false } });
      window.location.assign(cartUrl.toString().replace(/\.js$/, ''));
      return;
    }
    const cartResponse = await fetch(cartUrl.toString(), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) {
      deferred.reject(new Error('Handlekurven kunne ikke oppdateres.'));
      status.textContent = 'Produktet er lagt til. Åpne handlekurven øverst på siden.';
      return;
    }
    const cart = await cartResponse.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections: added.sections, itemCount: addedCount, source: 'varevo-product-form', didError: false },
    });
    status.textContent = addons.length ? 'MatSpar+ og valgt tilbehør er lagt i handlekurven.' : 'Lagt i handlekurven.';
  } catch (error) {
    deferred.reject(error);
    status.textContent = productAdded ? 'Produktet er lagt til. Åpne handlekurven øverst på siden.' : error.message || 'Noe gikk galt. Prøv igjen.';
    form.dispatchEvent(new CartErrorEvent({ error: status.textContent, code: 'SERVICE_UNAVAILABLE' }));
  } finally {
    pendingForms.delete(form);
    buttons.forEach((button, index) => { button.disabled = initialDisabled[index]; });
    addonInputs.forEach((input, index) => { input.disabled = initialAddonDisabled[index]; });
    updateAddonControls(form);
    form.removeAttribute('aria-busy');
  }
}

window.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-varevo-buy]');
  const form = button?.closest('form[data-varevo-cart="true"]');
  if (!form || button.disabled) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  submitVarevoCart({
    target: form,
    preventDefault() {},
    stopImmediatePropagation() {},
  });
}, { capture: true });

document.addEventListener('submit', submitVarevoCart, { capture: true });

